/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEye,
  FiX,
  FiMessageCircle,
  FiUsers,
  FiUserPlus,
  FiFileText,
  FiDollarSign,
  FiTrendingUp
} from "react-icons/fi";
import Swal from "sweetalert2";

import PageContainer from "../components/PageContainer";
import UnifiedCard from "../components/UnifiedCard";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import { formatNumber } from "../../utils/formatNumber";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  CartesianGrid,
} from "recharts";
import { FiMapPin, FiShoppingBag, FiStar } from "react-icons/fi";

// Using colors from useUI instead of hardcoded COLORS

export default function AdminCustomers() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const navigate = useNavigate();

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(0);
  const [selectedCity, setSelectedCity] = useState("");
  const [showView, setShowView] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cities, setCities] = useState([]);

  const parseOrderDate = (value) => {
    if (!value) return null;
    const fixed = value.replace(/\s+/g, " ").replace("AM", " AM").replace("PM", " PM");
    const d = new Date(fixed);
    return isNaN(d) ? null : d;
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch(`${baseUrl}/City/GetCities`);
        if (res.ok) {
          const data = await res.json();
          setCities(data);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
      }
    };
    fetchCities();
  }, [baseUrl]);

  const normalizeIraqGovernorate = (value = "") => {
    if (!value) return isArabic ? "غير محدد" : "Other";
    const foundCity = cities.find(c => c.cityName === value.trim() || c.cityId.toString() === value.toString());
    return foundCity ? foundCity.cityName : (value.trim() || (isArabic ? "غير محدد" : "Other"));
  };


  // Derive aggregated customer list from orders
  const aggregatedCustomers = useMemo(() => {
    const custMap = new Map();

    orders.forEach(o => {
      const phone = (o.customerPhone1 || "").trim();
      const name = (o.customerName || "").trim();
      if (!phone && !name) return; // Skip invalid entries

      // Unique key based on name only
      const key = name.toLowerCase();

      if (!custMap.has(key)) {
        custMap.set(key, {
          id: o.customerId || 0,
          name: name,
          phone: phone,
          address: o.address || "",
          city: o.city || "",
          orders: 0,
          totalSpent: 0,
          joinedDate: o.orderDate,
          latestOrderDate: o.orderDate
        });
      }

      const c = custMap.get(key);
      c.orders += 1;

      // Calculate total spent
      const total = Number(o.totalAmount || 0);
      let effectiveTotal = total > 0 ? total : 0;
      if (effectiveTotal === 0 && Array.isArray(o.details)) {
        effectiveTotal = o.details.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
      }
      c.totalSpent += effectiveTotal;

      // Update earliest date (Joined Date)
      const currentMin = parseOrderDate(c.joinedDate);
      const orderDate = parseOrderDate(o.orderDate);
      if (orderDate && (!currentMin || orderDate < currentMin)) {
        c.joinedDate = o.orderDate;
      }

      // Update info from latest order
      const currentMax = parseOrderDate(c.latestOrderDate);
      if (orderDate && (!currentMax || orderDate > currentMax)) {
        c.latestOrderDate = o.orderDate;
        c.phone = phone || c.phone;
        c.address = o.address || c.address;
        c.city = o.city || c.city;
      }
    });

    return Array.from(custMap.values()).sort((a, b) => b.orders - a.orders);
  }, [orders]);

  // Use the aggregated list as the source for customers
  const customers = aggregatedCustomers;

  // Fetch orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      if (!Number.isInteger(tenantId) || tenantId <= 0) return;

      try {
        setOrdersLoading(true);
        const token = localStorage.getItem("authToken");
        const params = new URLSearchParams();
        params.append('tenantId', tenantId.toString());
        params.append('statusId', '-1');

        const res = await fetch(`${baseUrl}/GetTenantOrders?${params.toString()}`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" }
        });

        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            // De-duplicate ONLY by unique orderId (UUID)
            const unique = [];
            const seenIds = new Set();
            data.forEach(o => {
              if (o.orderId && !seenIds.has(o.orderId)) {
                seenIds.add(o.orderId);
                unique.push(o);
              }
            });
            setOrders(unique);
          }
        }
      } catch (err) {
        console.error("Fetch orders error:", err);
      } finally {
        setOrdersLoading(false);
        setLoading(false);
      }
    };
    fetchOrders();
  }, [tenantId, baseUrl]);


  // Filter customers by period (new customers in last X months)
  const filteredByPeriod = useMemo(() => {
    if (period === 0) return customers;

    const now = new Date();
    const monthsAgo = new Date();
    monthsAgo.setMonth(now.getMonth() - period);

    return customers.filter((c) => {
      if (!c.joinedDate) return true;
      const joinedDate = new Date(c.joinedDate);
      if (isNaN(joinedDate)) return true;
      return joinedDate >= monthsAgo;
    });
  }, [customers, period]);

  const filtered = useMemo(() => {
    let result = customers;

    if (period > 0) {
      const monthsAgo = new Date();
      monthsAgo.setMonth(monthsAgo.getMonth() - period);

      result = result.filter((c) => {
        const d = parseOrderDate(c.joinedDate);
        if (!d) return true;
        return d >= monthsAgo;
      });
    }

    if (selectedCity) {
      result = result.filter(c => c.city === selectedCity);
    }

    return result;
  }, [customers, period, selectedCity]);

  const cityOptions = useMemo(() => {
    return Array.from(new Set(customers.map((c) => c.city))).filter(Boolean).sort();
  }, [customers]);

  const newCustomers = useMemo(() => {
    if (period === 0) return customers.length;
    const monthsAgo = new Date();
    monthsAgo.setMonth(monthsAgo.getMonth() - period);
    return customers.filter(c => {
      const d = parseOrderDate(c.joinedDate);
      return !d || d >= monthsAgo;
    }).length;
  }, [customers, period]);

  const orderStats = useMemo(() => {
    let fromDate = null;
    if (period > 0) {
      fromDate = new Date();
      fromDate.setMonth(fromDate.getMonth() - period);
    }

    const filteredOrders = orders.filter((o) => {
      if (!fromDate) return true;
      const d = parseOrderDate(o.orderDate);
      if (!d) return true; // Include if date is missing or invalid
      return d >= fromDate;
    });

    let totalValue = 0;
    let maxVal = 0;
    const govCount = {};
    const prodCount = {};
    const custOrderCount = {};

    filteredOrders.forEach((order) => {
      const total = Number(order.totalAmount || 0);
      let effectiveTotal = total > 0 ? total : 0;
      if (effectiveTotal === 0 && Array.isArray(order.details)) {
        effectiveTotal = order.details.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
      }
      totalValue += effectiveTotal;
      if (effectiveTotal > maxVal) maxVal = effectiveTotal;

      const gov = normalizeIraqGovernorate(order.city);
      govCount[gov] = (govCount[gov] || 0) + 1;

      const name = order.customerName || (isArabic ? "زبون مجهول" : "Unknown");
      custOrderCount[name] = (custOrderCount[name] || 0) + 1;

      (order.details || []).forEach(item => {
        const pName = item.itemName || item.ItemName;
        if (pName) prodCount[pName] = (prodCount[pName] || 0) + Number(item.quantity || 1);
      });
    });

    const getTopThree = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, 3);

    return {
      count: filteredOrders.length,
      value: totalValue,
      highest: maxVal,
      mostRequestedGovernorate: getTopThree(govCount),
      mostSoldProducts: getTopThree(prodCount),
      mostRequestedCustomer: getTopThree(custOrderCount),
      governoratesChart: Object.entries(govCount)
        .map(([name, value]) => ({
          name,
          value: filteredOrders.length > 0 ? ((value / filteredOrders.length) * 100).toFixed(1) : 0,
          count: value
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
    };
  }, [orders, period, cities, isArabic]);

  // Ranked List Component
  const RankedList = ({ items, color }) => (
    <div className="flex flex-col gap-3 mt-3 w-full">
      {items.length > 0 ? (
        items.map(([name, count], idx) => (
          <div key={idx} className="flex items-center justify-between w-full relative">
            <div className="flex items-center gap-3 overflow-hidden">
              <div
                className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white shrink-0"
                style={{ backgroundColor: color }}
              >
                {idx + 1}
              </div>
              <span
                className={`text-sm font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                title={name}
              >
                {name.length > 20 ? name.substring(0, 20) + "..." : name}
              </span>
            </div>
            <span
              className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0"
              style={{ backgroundColor: `${color}20`, color: color }}
            >
              {count}
            </span>
          </div>
        ))
      ) : (
        <span className="text-sm text-gray-400 text-center py-2">
          {isArabic ? "لا توجد بيانات" : "No Data"}
        </span>
      )}
    </div>
  );

  // Page-level permission guard (after all hooks)
  if (!perms.viewCustomers) {
    return <NoAccess />;
  }



  const totalCustomers = customers.length;


  return (
    <PageContainer title={isArabic ? "الزبائن" : "Customers"}>
      {/* ===== كروت الإحصائيات ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <UnifiedCard
          icon={FiUserPlus}
          title={isArabic ? "الزبائن الجدد" : "New Customers"}
          subtitle={
            isArabic
              ? (period === 0 ? "جميع الفترات" : `خلال آخر ${period === 1 ? "شهر" : `${period} أشهر`}`)
              : (period === 0 ? "All time" : `In last ${period} month${period === 1 ? "" : "s"}`)
          }
          value={newCustomers}
          valueColor={colors.mainColor}
          statCard={true}
        />

        <UnifiedCard
          icon={FiUsers}
          title={isArabic ? "إجمالي الزبائن" : "Total Customers"}
          subtitle={isArabic ? "منذ بداية المتجر" : "Since store started"}
          value={totalCustomers}
          valueColor={colors.green}
          statCard={true}
        />

        <UnifiedCard
          icon={FiFileText}
          title={isArabic ? "عدد الطلبات" : "Total Orders"}
          subtitle={isArabic ? "للفترة المحددة" : "For selected period"}
          value={formatNumber(orderStats.count)}
          valueColor={colors.blue}
          statCard={true}
        />

        <UnifiedCard
          icon={FiDollarSign}
          title={isArabic ? "قيمة الطلبات" : "Orders Value"}
          subtitle={isArabic ? "للفترة المحددة" : "For selected period"}
          value={`${formatNumber(Math.round(orderStats.value))} ${isArabic ? "د.ع" : "IQD"}`}
          valueColor={colors.green}
          statCard={true}
        />

        <UnifiedCard
          icon={FiTrendingUp}
          title={isArabic ? "أعلى طلب" : "Highest Order"}
          subtitle={isArabic ? "للفترة المحددة" : "For selected period"}
          value={`${formatNumber(Math.round(orderStats.highest))} ${isArabic ? "د.ع" : "IQD"}`}
          valueColor={colors.green}
          statCard={true}
        />
      </div>

      {/* ===== الفلترة والبحث وإضافة عميل ===== */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8 justify-between items-center">
        <div className="flex gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="rounded-xl px-4 py-2 text-sm shadow-sm"
            style={{
              backgroundColor: colors.inputBg,
              border: `1px solid ${colors.inputBorder}`,
              color: colors.text,
            }}
          >
            <option value={1}>{isArabic ? "آخر شهر" : "Last Month"}</option>
            <option value={3}>{isArabic ? "آخر 3 أشهر" : "Last 3 Months"}</option>
            <option value={6}>{isArabic ? "آخر 6 أشهر" : "Last 6 Months"}</option>
            <option value={0}>{isArabic ? "جميع الزبائن" : "All Customers"}</option>
          </select>

          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="rounded-xl px-4 py-2 text-sm shadow-sm"
            style={{
              backgroundColor: colors.inputBg,
              border: `1px solid ${colors.inputBorder}`,
              color: colors.text,
            }}
          >
            <option value="">{isArabic ? "كل المحافظات" : "All Governorates"}</option>
            {cityOptions.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
          <p className="mt-4" style={{ color: colors.subText }}>
            {isArabic ? "جاري تحميل بيانات العملاء..." : "Loading customers data..."}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p style={{ color: colors.subText }}>
            {isArabic ? "لا توجد عملاء متاحين" : "No customers available"}
          </p>
        </div>
      ) : (
        <>
          {/* ===== الجدول ===== */}
          <div className="w-full">
            <ResponsiveTable
              columns={[
                { header: isArabic ? "اسم الزبون" : "Name", sortKey: "name", align: "center" },
                { header: isArabic ? "الهاتف" : "Phone", sortKey: "phone", align: "center" },
                { header: isArabic ? "العنوان" : "Address", sortKey: "address", align: "center" },
                { header: isArabic ? "عدد الطلبات" : "Orders", sortKey: "orders", align: "center" },
                { header: isArabic ? "المحافظة" : "City", sortKey: "city", align: "center" },
                { header: isArabic ? "تحكم" : "Actions", align: "center" },
              ]}
              data={filtered}
              searchable={true}
              sortable={true}
              renderRow={(c) => (
                <>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    <span className="font-medium text-sm sm:text-base whitespace-nowrap">{c.name || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    <span className="text-sm sm:text-base whitespace-nowrap">{c.phone || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    <span className="text-sm sm:text-base whitespace-nowrap">{c.address || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="font-semibold text-sm sm:text-base whitespace-nowrap" style={{ color: colors.gold }}>
                      {c.orders || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                    <span className="text-sm sm:text-base whitespace-nowrap">{c.city || "—"}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center items-center gap-2 flex-nowrap whitespace-nowrap">
                      <a
                        href={`https://wa.me/${c.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 sm:p-2.5 rounded-lg text-white transition-all shadow-sm flex-shrink-0"
                        style={{ background: colors.green }}
                        title={isArabic ? "واتساب" : "WhatsApp"}
                      >
                        <FiMessageCircle size={16} />
                      </a>
                      <button
                        onClick={() => setShowView(c)}
                        className="p-2 sm:p-2.5 rounded-lg text-white transition-all shadow-sm flex-shrink-0"
                        style={{ background: colors.mainColor }}
                        title={isArabic ? "عرض" : "View"}
                      >
                        <FiEye size={16} />
                      </button>


                      <button
                        onClick={() => navigate(`/admin-store/customers/details/${encodeURIComponent(c.name)}`)}
                        className="p-2 sm:p-2.5 rounded-lg text-white transition-all shadow-sm flex-shrink-0"
                        style={{ background: colors.blue }}
                        title={isArabic ? "تفاصيل حساب العميل" : "Customer Details"}
                      >
                        <FiFileText size={16} />
                      </button>

                    </div>
                  </td>
                </>
              )}
              renderCard={(c) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                        {c.name}
                      </h3>
                      <p className="text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                        {c.phone}
                      </p>
                    </div>
                    <span
                      className="px-3 py-1 rounded-lg text-sm font-semibold"
                      style={{ color: colors.gold, backgroundColor: isDark ? "rgba(255,215,0,0.1)" : "rgba(242,180,65,0.1)" }}
                    >
                      {c.orders} {isArabic ? "طلبات" : "orders"}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm" style={{ color: colors.subText }}>
                    <p>{isArabic ? "العنوان:" : "Address:"} {c.address}</p>
                    <p>{isArabic ? "المحافظة:" : "City:"} {c.city}</p>
                  </div>
                  <div className="flex gap-2 pt-2 flex-nowrap overflow-x-auto">
                    <a
                      href={`https://wa.me/${c.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 min-w-0 p-2 rounded-lg text-white text-center text-sm transition-all shadow-sm "
                      style={{ background: colors.green }}
                    >
                      <FiMessageCircle className="inline mr-1" size={14} />
                      <span className="whitespace-nowrap">{isArabic ? "واتساب" : "WhatsApp"}</span>
                    </a>
                    <button
                      onClick={() => setShowView(c)}
                      className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all shadow-sm "
                      style={{ background: colors.mainColor }}
                    >
                      <FiEye className="inline mr-1" size={14} />
                      <span className="whitespace-nowrap">{isArabic ? "عرض" : "View"}</span>
                    </button>
                    <button
                      onClick={() => navigate(`/admin-store/customers/details/${encodeURIComponent(c.name)}`)}
                      className="flex-1 min-w-0 p-2 rounded-lg text-white text-sm transition-all shadow-sm"
                      style={{ background: colors.blue }}
                    >
                      <FiFileText className="inline mr-1" size={14} />
                      <span className="whitespace-nowrap">{isArabic ? "التفاصيل" : "Details"}</span>
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        </>
      )}



      {/* ===== المودالات ===== */}
      <AnimatePresence>
        {showView && (
          <CustomerModal
            isArabic={isArabic}
            isDark={isDark}
            colors={colors}
            title={isArabic ? "تفاصيل الزبون" : "Customer Details"}
            customer={showView}
            readOnly
            onClose={() => setShowView(null)}
          />
        )}
      </AnimatePresence>


    </PageContainer>
  );
}

/* ===== مودال عرض / تعديل الزبون ===== */
function CustomerModal({ isArabic, isDark, colors, title, customer, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "auto");
  }, []);


  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-[95%] sm:w-[90%] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl p-6 ${isDark ? "bg-[#1E293B] text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">

            <h2 className="text-xl font-bold" style={{ color: colors.mainColor }}>
              {title}
            </h2>
          </div>

          <button onClick={onClose} className="text-gray-400 transition">
            <FiX size={22} />
          </button>
        </div>


        {["name", "phone", "address", "city", "orders"].map((field) => (
          <div key={field} className="mb-4">
            <label className="block mb-2 font-semibold">
              {isArabic
                ? field === "name"
                  ? "اسم الزبون"
                  : field === "phone"
                    ? "الهاتف"
                    : field === "address"
                      ? "العنوان"
                      : field === "city"
                        ? "المحافظة"
                        : "عدد الطلبات"
                : field === "orders" ? "Orders" : field}
            </label>
            <input
              type={field === "orders" ? "number" : "text"}
              value={customer?.[field] ?? ""}
              readOnly
              disabled
              className="w-full rounded-lg border border-gray-600/40 px-3 py-2 bg-transparent opacity-60 cursor-not-allowed focus:outline-none"
            />

          </div>
        ))}
      </div>
    </motion.div>
  );
}


